import type React from 'react'
import { useCallback, useContext, useMemo, useState } from 'react'

import { Link } from '@tanstack/react-router'
import { sortBy } from 'lodash-es'
import { Clock, Flag, Map as MapIcon, X } from 'lucide-react'

import { AdminHeader } from '@/components/organisms/AdminHeader/AdminHeader'
import { Map, type MapData } from '@/components/organisms/Map'
import { MapTeamPositionContext } from '@/components/organisms/Map/Markers/MapTeamPositionProvider'
import { formatCost, formatMinutes } from '@/components/utils/formatCost'
import { useGoogleMapConfiguration } from '@/lib/useGoogleMapConfiguration.ts'
import { cn } from '@/lib/utils'
import { piloteIdToLetter } from '@/model/utils/PiloteIdToLetter.tsx'
import { AppPageMapContext } from '@/pages/dashboard/AppPageMapProvider'
import { teamStatus } from '@/pages/dashboard/data/teamStatus'
import { teamMapPositions } from '@/state/assignments/teamMapPositions'
import {
  type AssignmentCosts,
  type AssignmentTeam,
  type AssignmentTransportRequest,
  type RegionAssignment,
  type TeamTransportRequestAssignmentCosts,
} from '@/state/assignments/types'

export interface RegionAssignmentDetailsViewProps {
  assignment: RegionAssignment
}

const getStatusColor = (team: AssignmentTeam): { fill: string; border?: string } => {
  if (!team.transportRequest?.status) {
    return { fill: 'bg-gray-400' }
  }
  switch (team.transportRequest.status) {
    case 'assigned':
      return { fill: 'bg-transparent', border: 'border-2 border-blue-600' }
    case 'allocated':
      return { fill: 'bg-blue-600' }
    case 'on_board':
      return { fill: 'bg-green-500' }
    default:
      return { fill: 'bg-gray-400' }
  }
}

const hasManualTransmissionBar = (team: AssignmentTeam): boolean => team.vehicle?.manual ?? false

interface ComparisonItem {
  transportRequestId: string
  teamId: string
  transportRequest: AssignmentTransportRequest
  team: AssignmentTeam
  costs: TeamTransportRequestAssignmentCosts
  solverSelected: boolean
  rank: number
}

export const RegionAssignmentDetailsView: React.FC<RegionAssignmentDetailsViewProps> = ({ assignment }) => {
  const [comparisonItems, setComparisonItems] = useState<ComparisonItem[]>([])
  const [isMapOpen, setIsMapOpen] = useState(false)

  const toggleComparison = useCallback((item: ComparisonItem) => {
    setComparisonItems((prev) => {
      const exists = prev.some((i) => i.transportRequestId === item.transportRequestId && i.teamId === item.teamId)
      if (exists) {
        return prev.filter((i) => !(i.transportRequestId === item.transportRequestId && i.teamId === item.teamId))
      }
      return [...prev, item]
    })
  }, [])

  const removeFromComparison = useCallback((transportRequestId: string, teamId: string) => {
    setComparisonItems((prev) =>
      prev.filter((i) => !(i.transportRequestId === transportRequestId && i.teamId === teamId))
    )
  }, [])

  const clearComparison = useCallback(() => {
    setComparisonItems([])
  }, [])

  const isInComparison = useCallback(
    (transportRequestId: string, teamId: string): boolean =>
      comparisonItems.some((i) => i.transportRequestId === transportRequestId && i.teamId === teamId),
    [comparisonItems]
  )

  const googleMapConfiguration = useGoogleMapConfiguration()
  const { onMapLoad, onMapUnmount } = useContext(AppPageMapContext)

  const teams = useMemo(
    () => sortBy(assignment.teams, (team) => assignment.costs.details.teams[team._id]?.cost ?? 0),
    [assignment]
  )

  const transportRequests = useMemo(
    () => sortBy(assignment.transportRequests, (tr) => assignment.costs.details.transportRequests[tr._id]?.cost ?? 0),
    [assignment]
  )

  const teamPositions = useMemo(() => teamMapPositions(assignment.teams, assignment.costs), [assignment])

  const { _id: assignmentId, region, previousId, nextId, costs, timestamp, solver } = assignment

  const getRankedCosts = useCallback(
    (transportRequestId: string) => {
      const assignments = costs.details.assignments?.[transportRequestId]
      if (!assignments) return {}
      const finiteEntries = Object.entries(assignments).filter(([, a]) => a.cost != null && Number.isFinite(a.cost))
      const sorted = finiteEntries.sort(([, a], [, b]) => a.cost - b.cost)
      return Object.fromEntries(sorted.map(([teamId], index) => [teamId, index + 1]))
    },
    [costs.details.assignments]
  )

  const comparisonTotalCost = useMemo(() => {
    const hasInfinite = comparisonItems.some((item) => !Number.isFinite(item.costs.cost))
    if (hasInfinite) return Infinity
    return comparisonItems.reduce((sum, item) => sum + item.costs.cost, 0)
  }, [comparisonItems])

  const { selectedTeamIds, selectedTransportRequestIds } = useMemo(() => {
    if (comparisonItems.length === 0) {
      return {
        selectedTeamIds: [] as string[],
        selectedTransportRequestIds: [] as string[],
      }
    }
    return {
      selectedTeamIds: [...new Set(comparisonItems.map((i) => i.teamId))],
      selectedTransportRequestIds: [...new Set(comparisonItems.map((i) => i.transportRequestId))],
    }
  }, [comparisonItems])

  const mapData = useMemo(
    () => ({
      zoom: 11,
      center: {
        lat: 46.8139,
        lng: -71.208,
      },
      ...(region?.mapConfiguration && {
        zoom: region.mapConfiguration.zoom,
        center: {
          lat: region.mapConfiguration.center?.latitude ?? 46.8139,
          lng: region.mapConfiguration.center?.longitude ?? -71.208,
        },
      }),
      transportRequests: [
        ...transportRequests.map(({ _id, piloteId, origin, destination }) => ({
          id: _id,
          status: 'pending-origin' as const,
          locationLabel: piloteId != null ? piloteIdToLetter(piloteId) : '?',
          origin: {
            lat: origin.geolocation.latitude,
            lng: origin.geolocation.longitude,
          },
          destination: {
            lat: destination.geolocation.latitude,
            lng: destination.geolocation.longitude,
          },
        })),
        ...teams.flatMap<NonNullable<MapData['transportRequests']>[number]>((team) => {
          if (!team.currentTransportRequest || !team.transportRequest?.status) {
            return []
          }
          const origin = {
            lat: team.currentTransportRequest.origin.geolocation.latitude,
            lng: team.currentTransportRequest.origin.geolocation.longitude,
          }
          const destination = {
            lat: team.currentTransportRequest.destination.geolocation.latitude,
            lng: team.currentTransportRequest.destination.geolocation.longitude,
          }
          switch (team.transportRequest.status) {
            case 'assigned':
              return [
                {
                  id: team.currentTransportRequest._id,
                  status: 'team-assigned' as const,
                  locationLabel: String(team.number ?? ''),
                  origin,
                  destination,
                },
              ]
            case 'allocated':
              return [
                {
                  id: team.currentTransportRequest._id,
                  status: 'team-allocated' as const,
                  locationLabel: String(team.number ?? ''),
                  origin,
                  destination,
                },
              ]
            case 'on_board':
              return [
                {
                  id: team.currentTransportRequest._id,
                  status: 'team-on-board' as const,
                  locationLabel: String(team.number ?? ''),
                  origin,
                  destination,
                },
              ]
            default:
              return []
          }
        }),
      ],
      teams: teams.map(({ _id, status, number }) => ({
        id: _id,
        status: teamStatus(status) ?? ('available' as const),
        carNumber: String(number ?? ''),
      })),
    }),
    [region, teams, transportRequests]
  )

  return (
    <div className="flex h-screen flex-col">
      <AdminHeader title="Plan" />
      <div className="flex min-h-0 flex-1 bg-white">
        <div className="flex max-w-[50%] flex-col border-r border-gray-200">
          <div className="flex-1 overflow-auto p-4">
            <div className="flex items-center gap-8 pl-[100px] text-[12px] text-gray-500">
              {previousId ? (
                <Link
                  to="/assignments/$assignmentId"
                  params={{ assignmentId: previousId }}
                  search={{ region_id: undefined, limit: undefined }}
                  className="hover:text-gray-700">
                  {'< PREVIOUS'}
                </Link>
              ) : (
                <span className="text-gray-300">{'< PREVIOUS'}</span>
              )}
              {nextId ? (
                <Link
                  to="/assignments/$assignmentId"
                  params={{ assignmentId: nextId }}
                  search={{ region_id: undefined, limit: undefined }}
                  className="hover:text-gray-700">
                  {'NEXT >'}
                </Link>
              ) : (
                <span className="text-gray-300">{'NEXT >'}</span>
              )}
              <Link
                to="/assignments"
                search={{ region_id: undefined, limit: undefined }}
                className="hover:text-gray-700">
                LIST
              </Link>
              <a
                href={`/plans/${assignmentId}`}
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-gray-700">
                LEGACY
              </a>
            </div>
            <div className="mt-3 pl-[100px]">
              <div className="font-['Open_Sans_Variable'] text-[25px] text-black">
                {region?.name ?? 'Unknown Region'}
              </div>
              <div className="text-sm text-black">
                {timestamp.toLocaleString()} · {costs.formula === 'minutes' ? 'Minutes formula' : 'Points formula'}
              </div>
            </div>
            <div className="mt-2 flex gap-1 pb-1">
              <div className="w-[96px] shrink-0" />
              {teams.map((team) => {
                const statusColors = getStatusColor(team)
                return (
                  <div key={team._id} className="flex h-2 w-[76px] shrink-0 flex-col justify-center gap-px">
                    {hasManualTransmissionBar(team) && <div className="h-0.5 w-full rounded-sm bg-black" />}
                    <div className={cn('h-full w-full rounded-sm', statusColors.fill, statusColors.border)} />
                  </div>
                )
              })}
            </div>

            <div className="flex gap-1">
              <div className="flex h-12 w-[96px] shrink-0 flex-col justify-center rounded-sm px-2 py-1">
                <div className="flex w-full items-center gap-1">
                  <span className="text-[10px] text-black">Solver</span>
                  <span className="ml-auto text-right text-sm text-black">{formatCost(solver?.cost ?? 0)}</span>
                </div>
                {comparisonItems.length > 0 && (
                  <div className="flex w-full items-center gap-1">
                    <span className="text-[10px] text-gray-500">Sel.</span>
                    <span className="ml-auto text-right text-sm text-gray-500">
                      {Number.isFinite(comparisonTotalCost) ? formatCost(comparisonTotalCost) : '∞'}
                    </span>
                  </div>
                )}
              </div>
              {teams.map((team) => {
                const teamCosts = costs.details.teams[team._id]
                const isWaiting = teamCosts?.availability === 'now'
                const displayValue = isWaiting
                  ? (teamCosts?.idleTimeInMinutes ?? 0)
                  : teamCosts?.availability === 'unknown'
                    ? '?'
                    : (teamCosts?.availability ?? 0)
                return (
                  <div
                    key={team._id}
                    className="flex h-12 w-[76px] shrink-0 flex-col items-end justify-center rounded-sm bg-black px-2 py-1 text-white">
                    <div className="flex w-full items-center justify-between">
                      <span className="text-xl font-bold">{team.number}</span>
                      <div className="flex items-center gap-0.5">
                        <span className="text-[10px]">{Math.round(Number(displayValue) || 0)}</span>
                        {isWaiting ? <Clock className="h-3 w-3" /> : <Flag className="h-3 w-3" />}
                      </div>
                    </div>
                    <span className="w-full text-sm text-blue-200">{formatCost(teamCosts?.cost ?? 0)}</span>
                  </div>
                )
              })}
            </div>

            <div className="flex flex-col gap-2 pt-2">
              {transportRequests.map((transportRequest) => {
                const trCosts = costs.details.transportRequests[transportRequest._id]
                const rankedCosts = getRankedCosts(transportRequest._id)

                return (
                  <div key={transportRequest._id} className="flex gap-1">
                    <div className="flex h-12 w-[96px] shrink-0 flex-col items-end justify-center rounded-sm bg-black px-2 py-1 text-white">
                      <div className="flex w-full items-center justify-between">
                        <span className="text-xl font-bold">
                          {transportRequest.piloteId != null ? piloteIdToLetter(transportRequest.piloteId) : '?'}
                        </span>
                        <div className="flex items-center gap-0.5">
                          <span className="text-[10px]">{trCosts?.waitTime ?? 0}</span>
                          <Clock className="h-3 w-3" />
                        </div>
                      </div>
                      <span className="w-full text-sm text-blue-200">{formatCost(trCosts?.cost ?? 0)}</span>
                    </div>

                    {teams.map((team) => {
                      const solverCosts = costs.details.assignments?.[transportRequest._id]?.[team._id]
                      const solverSelected = solver?.assignations?.[transportRequest._id] === team._id
                      const isInfiniteCost = !solverCosts || !Number.isFinite(solverCosts.cost)
                      const rank = isInfiniteCost ? undefined : rankedCosts[team._id]
                      const inComparison = isInComparison(transportRequest._id, team._id)

                      if (!solverCosts || isInfiniteCost) {
                        return (
                          <div
                            key={team._id}
                            className="flex h-12 w-[76px] shrink-0 flex-col items-start justify-end rounded-sm border border-gray-200 px-1 py-1.5">
                            <span className="text-[14px] leading-[1.2] font-bold text-gray-500">∞</span>
                          </div>
                        )
                      }

                      const handleClick = () => {
                        const tr = transportRequests.find((t) => t._id === transportRequest._id)
                        const tm = teams.find((t) => t._id === team._id)
                        if (tr && tm && rank != null) {
                          toggleComparison({
                            transportRequestId: transportRequest._id,
                            teamId: team._id,
                            transportRequest: tr,
                            team: tm,
                            costs: solverCosts,
                            solverSelected,
                            rank,
                          })
                        }
                      }

                      return (
                        <button
                          key={team._id}
                          onClick={handleClick}
                          className={cn(
                            'flex h-12 w-[76px] shrink-0 cursor-pointer flex-col items-start justify-end rounded-sm px-1 py-1.5 text-left transition-colors',
                            inComparison && solverSelected
                              ? 'border-2 border-black bg-green-400'
                              : inComparison
                                ? 'border-2 border-gray-800 bg-gray-100'
                                : solverSelected
                                  ? 'bg-green-300'
                                  : 'border border-gray-200 hover:bg-gray-50'
                          )}>
                          <span
                            className={cn(
                              'text-[12px] leading-[1.2]',
                              solverSelected || inComparison ? 'text-black' : 'text-gray-500'
                            )}>
                            {rank}
                          </span>
                          <span
                            className={cn(
                              'text-[14px] leading-[1.2] font-bold',
                              solverSelected || inComparison ? 'text-black' : 'text-gray-500'
                            )}>
                            {formatCost(solverCosts.cost)}
                          </span>
                        </button>
                      )
                    })}
                  </div>
                )
              })}
            </div>
          </div>
        </div>

        <div
          className={cn(
            'shrink-0 overflow-hidden border-l border-gray-200 bg-gray-100 transition-all duration-300 ease-in-out',
            comparisonItems.length > 0 ? 'w-auto max-w-[300px]' : 'max-w-0 border-l-0'
          )}>
          <div className="flex h-full w-auto flex-col">
            <div className="flex shrink-0 items-center gap-3 border-b border-gray-200 bg-gray-100 px-4 py-3">
              <button onClick={clearComparison} className="text-gray-500 hover:text-gray-800">
                <X className="h-4 w-4" />
              </button>
              <span className="text-sm text-black">Comparaison</span>
            </div>

            <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto p-2">
              {comparisonItems.map((item) => (
                <ComparisonCard
                  key={`${item.transportRequestId}-${item.teamId}`}
                  item={item}
                  formula={costs.formula}
                  onRemove={() => {
                    removeFromComparison(item.transportRequestId, item.teamId)
                  }}
                />
              ))}
            </div>
          </div>
        </div>

        {!isMapOpen && (
          <button
            onClick={() => {
              setIsMapOpen(true)
            }}
            className="flex w-10 shrink-0 flex-col items-center justify-center border-l border-gray-200 bg-gray-50 transition-colors hover:bg-gray-100">
            <MapIcon className="h-5 w-5 text-gray-600" />
          </button>
        )}

        <div
          className={cn(
            'flex min-h-0 flex-col overflow-hidden border-l border-gray-200 transition-all duration-300 ease-in-out',
            isMapOpen ? 'flex-1' : 'w-0 border-l-0'
          )}>
          <div className="flex shrink-0 items-center justify-between border-b border-gray-200 bg-gray-50 px-3 py-2">
            <span className="text-sm font-medium text-gray-700">Carte</span>
            <button
              onClick={() => {
                setIsMapOpen(false)
              }}
              className="rounded p-1 text-gray-500 transition-colors hover:bg-gray-200 hover:text-gray-800">
              <X className="h-4 w-4" />
            </button>
          </div>

          <div className="min-h-0 flex-1">
            <MapTeamPositionContext.Provider value={teamPositions}>
              <Map
                className="h-full w-full"
                height="100%"
                data={mapData}
                activeView="all"
                onMapLoad={onMapLoad}
                onMapUnmount={onMapUnmount}
                googleMapConfiguration={googleMapConfiguration}
                selectedTeamIds={selectedTeamIds}
                selectedTransportRequestIds={selectedTransportRequestIds}
                fitBoundsToVisibleMarkers={comparisonItems.length > 0}
              />
            </MapTeamPositionContext.Provider>
          </div>
        </div>
      </div>
    </div>
  )
}

interface ComparisonCardProps {
  item: ComparisonItem
  formula: AssignmentCosts['formula']
  onRemove: () => void
}

const PointsCostDetails: React.FC<{ costs: TeamTransportRequestAssignmentCosts }> = ({ costs }) => (
  <>
    <div className="flex gap-1">
      <CostDetail label="Dist. Cost" value={String(formatCost(costs.distanceCost))} bold />
      <CostDetail label="Est. Dist. Cost" value={String(formatCost(costs.estimatedDistanceCost))} bold />
    </div>
    <div className="flex gap-1">
      <CostDetail label="Veh. Type Cost" value={String(costs.vehicleTypeCost ?? 0)} />
      <CostDetail label="Reg. Change Pen." value={String(costs.regionChangePenalty ?? 0)} />
    </div>
    <div className="flex gap-1">
      <CostDetail label="Assign. Cost" value={String(formatCost(costs.assignmentCost))} />
      <CostDetail label="Est. Cost" value={String(formatCost(costs.estimatedDistanceCost))} />
    </div>
    <div className="flex gap-1">
      <CostDetail label="Real Distance" value={String(formatCost((costs.estimatedDistance ?? 0) / 1000))} />
    </div>
  </>
)

const MinutesCostDetails: React.FC<{ costs: TeamTransportRequestAssignmentCosts }> = ({ costs }) => (
  <>
    <div className="flex gap-1">
      <CostDetail label="Waited" value={String(formatMinutes(costs.waitedMinutes))} />
      <CostDetail label="Avail." value={String(formatMinutes(costs.availabilityMinutes))} />
    </div>
    <div className="flex gap-1">
      <CostDetail label="Travel" value={String(formatMinutes(costs.travelMinutes))} />
      <CostDetail label="Outside SP" value={String(formatMinutes(costs.outsideServicePointMinutes))} />
    </div>
    <div className="flex gap-1">
      <CostDetail label="Penalty" value={String(formatMinutes(costs.penaltyMinutes))} />
      <CostDetail label="Equity" value={String(formatMinutes(costs.equityMinutes))} />
    </div>
    <div className="flex gap-1">
      <CostDetail label="Total wait" value={String(formatMinutes(costs.totalWaitMinutes))} bold />
      <CostDetail label="Weighted" value={String(formatMinutes(costs.weightedMinutes))} bold />
    </div>
  </>
)

export const PairCostDetails: React.FC<{
  formula: AssignmentCosts['formula']
  costs: TeamTransportRequestAssignmentCosts
}> = ({ formula, costs }) =>
  formula === 'minutes' ? <MinutesCostDetails costs={costs} /> : <PointsCostDetails costs={costs} />

const ComparisonCard: React.FC<ComparisonCardProps> = ({ item, formula, onRemove }) => {
  const { transportRequest, team, costs, solverSelected, rank } = item

  return (
    <div className="relative rounded-sm bg-white p-2.5">
      <button
        onClick={() => {
          onRemove()
        }}
        className="absolute top-1 right-1 text-gray-400 hover:text-gray-600">
        <X className="h-3 w-3" />
      </button>
      <div className="flex items-start justify-between">
        <div className="flex w-[77px] flex-col gap-1">
          <div className="flex h-8 items-center justify-between gap-4 rounded-sm bg-black px-2 text-white">
            <span className="text-xl font-bold">
              {transportRequest.piloteId != null ? piloteIdToLetter(transportRequest.piloteId) : '?'}
            </span>
            <span className="text-xl font-bold">{team.number}</span>
          </div>

          <div
            className={cn(
              'flex h-12 flex-col items-start justify-between rounded-sm px-1 py-1',
              solverSelected ? 'bg-green-300' : 'border border-gray-200'
            )}>
            <span className={cn('text-xs leading-[1.2]', solverSelected ? 'text-black' : 'text-gray-500')}>{rank}</span>
            <span className={cn('text-sm leading-[1.2] font-bold', solverSelected ? 'text-black' : 'text-gray-500')}>
              {formatCost(costs.cost)}
            </span>
          </div>
        </div>

        <div className="flex w-[141px] flex-col gap-2 p-2">
          <PairCostDetails formula={formula} costs={costs} />
        </div>
      </div>
    </div>
  )
}

interface CostDetailProps {
  label: string
  value: string
  bold?: boolean
}

const CostDetail: React.FC<CostDetailProps> = ({ label, value, bold }) => (
  <div className="flex min-w-0 flex-1 flex-col gap-0.5">
    <span className="text-[7px] whitespace-nowrap text-gray-500 uppercase">{label}</span>
    <span className={cn('text-xs text-black', bold && 'font-bold')}>{value}</span>
  </div>
)
